'use client'

import { LOSI_GAMES } from '@/lib/losi-games/catalog'
import { bestLocalScore } from '@/lib/losi-games/leaderboard'
import type { LosiGameId } from '@/lib/losi-games/types'

interface LosiGamesHubProps {
  onPlay: (id: LosiGameId) => void
}

// Vibecode (and its saved library) are temporarily out of the arcade, along
// with Pinball and Match. The catalog entries stay; we filter them here so
// restoring is a one-line change. Super agents were permanently removed.
const HIDDEN_GAME_IDS: ReadonlySet<LosiGameId> = new Set<LosiGameId>([
  'vibecode',
  'pinball',
  'eye-match'
])

export function LosiGamesHub({ onPlay }: LosiGamesHubProps) {
  const games = LOSI_GAMES.filter((game) => !HIDDEN_GAME_IDS.has(game.id))

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-5 py-10">
      <header className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight text-white">Losi Arcade</h1>
        <p className="max-w-xl text-sm leading-6 text-white/55">
          Arcade loops rendered with Losi&rsquo;s own glyphs. Pick a game, chase a private
          best — every run is animated by the character you already know.
        </p>
      </header>

      <section
        aria-label="Games"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {games.map((game) => (
          <button
            key={game.id}
            type="button"
            onClick={() => onPlay(game.id)}
            className="group relative flex min-h-[9.5rem] flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left transition duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <div className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 transition group-hover:opacity-100" />
            <div>
              <div className="text-lg font-medium text-white">{game.title}</div>
              <div className="mt-1.5 text-sm leading-5 text-white/50">{game.blurb}</div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-white/35">
              <span>Eyes as {game.eyeRole}</span>
              <span className="tabular-nums text-white/45">best {bestLocalScore(game.id)}</span>
            </div>
          </button>
        ))}
      </section>
    </div>
  )
}
